/**
 * Brand logo map marker (SAM-5345).
 *
 * Wraps a brand/restaurant logo in the same pin shape used by the built-in
 * task markers (see assets/theme/icons/task-types/PICK_UP.svg), with the logo
 * clipped to the circular area of the pin instead of floating on the map.
 *
 * The result is an SVG data URI, so it works with any map client's
 * `addIconMarker` / `setIcon`.
 */

const PIN_WIDTH = 35;
const PIN_HEIGHT = 40;
const CIRCLE = { cx: 17.5, cy: 17.6, r: 13.3 };
// Rasterised logo size (px). Plenty for a ~40px marker, incl. retina.
const LOGO_PX = 128;

const CACHE_MAX = 100;
const cache = new Map();

/** Load the logo through a canvas so it can be embedded in the SVG data URI
 * (an <img>-rendered SVG may not load external images). Resolves with a
 * square, centre-cropped PNG data URL, or rejects (e.g. missing CORS). */
function logoToDataUrl(url) {
  return new Promise((resolve, reject) => {
    if (typeof Image === "undefined" || typeof document === "undefined") {
      reject(new Error("No DOM available"));
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) throw new Error("Empty logo image");
        const side = Math.min(w, h);
        const canvas = document.createElement("canvas");
        canvas.width = LOGO_PX;
        canvas.height = LOGO_PX;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, LOGO_PX, LOGO_PX);
        ctx.drawImage(
          img,
          (w - side) / 2,
          (h - side) / 2,
          side,
          side,
          0,
          0,
          LOGO_PX,
          LOGO_PX
        );
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Failed to load brand logo: " + url));
    img.src = url;
  });
}

/** Pure builder: pin SVG data URI around an already-embeddable logo URL. */
export function buildBrandPinSvg(logoDataUrl) {
  const { cx, cy, r } = CIRCLE;
  const svg = `<svg width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="0 0 ${PIN_WIDTH} ${PIN_HEIGHT}" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
<defs><clipPath id="brandLogoClip"><circle cx="${cx}" cy="${cy}" r="${r}"/></clipPath></defs>
<path d="M17.5 1C26.4033 1 34 8.60324 34 17.8516C33.9997 27.0497 26.3115 33.9149 18.793 38.6514C18.3958 38.8804 17.9536 39 17.5 39C17.0506 39 16.6121 38.8831 16.2178 38.6582C8.69712 33.8676 1.00027 27.236 1 17.8516C1 8.42213 8.58986 1 17.5 1Z" fill="#18181B" stroke="#FAFAFA" stroke-width="2"/>
<circle cx="${cx}" cy="${cy}" r="${r}" fill="#FFFFFF"/>
<image xlink:href="${logoDataUrl}" href="${logoDataUrl}" x="${cx - r}" y="${cy - r}" width="${r * 2}" height="${r * 2}" preserveAspectRatio="xMidYMid slice" clip-path="url(#brandLogoClip)"/>
</svg>`;
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

/**
 * Resolve with a pin-shaped marker icon URL containing the brand logo.
 * Resolves with null if the logo can't be embedded; callers should keep
 * their fallback icon in that case.
 */
export function getBrandMarkerIcon(logoUrl) {
  if (typeof logoUrl !== "string" || logoUrl.trim() === "") {
    return Promise.resolve(null);
  }
  if (!cache.has(logoUrl)) {
    if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
    cache.set(
      logoUrl,
      logoToDataUrl(logoUrl)
        .then(buildBrandPinSvg)
        .catch((e) => {
          cache.delete(logoUrl);
          if (process.env.NODE_ENV !== "production") {
            console.warn("Brand marker fallback:", e.message);
          }
          return null;
        })
    );
  }
  return cache.get(logoUrl);
}
